import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Row,
  Table,
  Container,
  Card,
  Modal,
  Badge,
} from "react-bootstrap";
import { ShimmerTable } from "react-shimmer-effects";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { FaEye, FaWhatsapp, FaExternalLinkAlt, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import jwt_decode from "jwt-decode";

const AdManagerList = ({ selectedWhatsAppSetting }) => {
  const [ads, setAds] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token"))
  );

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole === "USER" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      // Keep last 5 digits visible, mask the rest with X
      const numberString = String(number);
      if (numberString.length <= 5) {
        return numberString; // Show all if number is 5 digits or less
      }
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  useEffect(() => {
    if (selectedWhatsAppSetting) {
      fetchData(selectedWhatsAppSetting);
    }
  }, [selectedWhatsAppSetting]);

  const fetchData = async (businessNumber) => {
    try {
      setIsSpinner(false);
      const result = await WhatsAppAPI.getAdManagerRecords(businessNumber);
      if (result.success) {
        // Add formatted phone numbers to the ads data
        const formattedAds = result.records.map((ad) => ({
          ...ad,
          // Format the whatsapp_numbers array if it exists
          formatted_whatsapp_numbers:
            ad.whatsapp_numbers?.map((number) => formatPhoneNumber(number)) ||
            [],
        }));
        setAds(formattedAds);
      } else {
        setAds([]);
      }
    } catch (error) {
      toast.error("Error fetching ads");
      console.error("Error fetching ads:", error);
    } finally {
      setIsSpinner(true);
    }
  };

  const handleViewMessages = (ad) => {
    setSelectedAd(ad);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAd(null);
  };

  const handleRedirectToLeads = (parentId, name, phone) => {
    if (parentId) {
      navigate(`/leads/${parentId}`, {
        state: {
          leadName: name,
          leadPhone: phone,
          fromAdManager: true,
          adHeadline: selectedAd?.headline,
        },
      });
      handleModalClose();
    } else {
      toast.error("No lead ID available for this contact");
    }
  };

  // Format platform name
  const formatPlatform = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  // Main table headers
  const header = [
    {
      title: "Platform",
      prop: "platform",
      cell: (row) => (
        <Badge bg="primary" className="text-capitalize">
          {formatPlatform(row.platform)}
        </Badge>
      ),
    },
    {
      title: "Ad Headline",
      prop: "headline",
      isFilterable: true,
      cell: (row) => (
        <div>
          <div className="fw-semibold">{row.headline || "No Headline"}</div>
          {row.ad_url && (
            <small>
              <a
                href={row.ad_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted"
              >
                View Ad <FaExternalLinkAlt size={10} />
              </a>
            </small>
          )}
        </div>
      ),
    },
    {
      title: "Ad Content",
      prop: "body",
      cell: (row) => (
        <div
          className="text-truncate"
          style={{ maxWidth: "200px" }}
          title={row.body}
        >
          {row.body || "No content"}
        </div>
      ),
    },
    {
      title: "Media",
      prop: "file_url",
      cell: (row) => {
        if (!row.file_url) {
          return <span className="text-muted">No media</span>;
        }

        const isVideo = row.media_type === "video";
        const isImage = row.media_type === "image";

        return (
          <div style={{ width: "60px" }}>
            {isImage && (
              <img
                src={row.file_url}
                alt="Ad media"
                style={{
                  width: "50px",
                  height: "50px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
                onError={(e) => (e.target.style.display = "none")}
              />
            )}

            {isVideo && (
              <video
                src={row.file_url}
                style={{
                  width: "50px",
                  height: "50px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
                muted
                playsInline
                onError={(e) => (e.target.style.display = "none")}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Total Messages",
      prop: "total_messages",
      cell: (row) => (
        <Badge bg={parseInt(row.total_messages) > 0 ? "success" : "secondary"}>
          {row.total_messages} leads
        </Badge>
      ),
    },
    {
      title: "Actions",
      prop: "actions",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <Button
            variant={
              parseInt(row.total_messages) > 0 ? "primary" : "outline-secondary"
            }
            size="sm"
            onClick={() => handleViewMessages(row)}
            disabled={parseInt(row.total_messages) === 0}
          >
            <FaEye />{" "}
            {parseInt(row.total_messages) > 0
              ? `View Leads (${row.total_messages})`
              : "No Leads"}
          </Button>
        </div>
      ),
    },
  ];

  const labels = {
    beforeSelect: " ",
  };

  return (
    <>
      <Container className="mt-5 mb-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">📢 Ad Manager Leads</h5>
            <small>
              Track all ads with unique WhatsApp leads and total message count.
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <div className="d-flex justify-content-between align-items-center mb-1 ">
                    <h5 className="fw-semibold mb-0">📦 All Ads</h5>
                  </div>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={ads}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={6}
                          sm={10}
                          xs={10}
                          className="d-flex justify-content-start align-items-end"
                        >
                          <Filter className="p-2" />
                        </Col>
                        <Col
                          lg={6}
                          sm={2}
                          xs={2}
                          className="d-flex justify-content-end align-items-start"
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table
                          striped
                          bordered
                          className="related-list-table"
                          responsive="lg"
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={7} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Leads Modal */}
      {/* Leads Modal */}
      <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUser className="me-2" />
            Leads for Ad: {selectedAd?.headline}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAd && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Platform:</strong>{" "}
                  {formatPlatform(selectedAd.platform)}
                </Col>
                <Col md={6}>
                  <strong>Total Leads:</strong> {selectedAd.total_messages}
                </Col>
              </Row>

              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone Number</th>
                      <th>Last Message</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAd.contacts && selectedAd.contacts.length > 0 ? (
                      selectedAd.contacts.map((contact, index) => (
                        <tr key={index}>
                          <td className="fw-semibold">
                            {contact.name || "Unknown"}
                          </td>
                          <td>
                            <code>
                              {formatPhoneNumber(contact.whatsapp_number)}
                            </code>
                          </td>
                          <td>
                            <div
                              className="text-truncate"
                              style={{ maxWidth: "200px" }}
                              title={contact.last_message}
                            >
                              {contact.last_message || "No message"}
                            </div>
                          </td>

                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() =>
                                handleRedirectToLeads(
                                  contact.parent_id,
                                  contact.name,
                                  contact.whatsapp_number
                                )
                              }
                              disabled={!contact.parent_id}
                              title={
                                !contact.parent_id
                                  ? "No parent ID available"
                                  : "View lead details"
                              }
                            >
                              <FaUser /> View Lead
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No leads available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </>
  );
};

export default AdManagerList;
